import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "./AuthContext";

const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

export function SiteProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = !!user && user.role === "admin";

  const [content, setContent] = useState({});
  const [options, setOptions] = useState({});
  const [editMode, setEditModeState] = useState(() => localStorage.getItem("uptoo_edit_mode") === "1");

  const setEditMode = useCallback((v) => {
    setEditModeState((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      if (next) localStorage.setItem("uptoo_edit_mode", "1");
      else localStorage.removeItem("uptoo_edit_mode");
      return next;
    });
  }, []);

  const loadContent = useCallback(() => {
    api.get("/content").then(({ data }) => setContent(data || {})).catch(() => {});
  }, []);
  const loadOptions = useCallback(() => {
    api.get("/options").then(({ data }) => setOptions(data || {})).catch(() => {});
  }, []);

  useEffect(() => { loadContent(); loadOptions(); }, [loadContent, loadOptions]);
  // Only clear for a confirmed non-admin. During auth loading `user` is null — don't wipe edit mode then.
  useEffect(() => { if (user && user.role !== "admin") setEditMode(false); }, [user, setEditMode]);

  const saveText = useCallback(async (key, value, def) => {
    const normalized = value === def ? "" : value; // reverting to default clears the override
    setContent((c) => {
      const next = { ...c };
      if (normalized) next[key] = normalized; else delete next[key];
      return next;
    });
    try {
      await api.put("/admin/content", { key, value: normalized });
      toast.success("Saved", { id: "content-save", duration: 1200 });
    } catch {
      toast.error("Could not save text");
      loadContent();
    }
  }, [loadContent]);

  const saveOptions = useCallback(async (group, list) => {
    setOptions((o) => ({ ...o, [group]: list }));
    try {
      const { data } = await api.put("/admin/options", { group, options: list });
      setOptions(data);
    } catch {
      toast.error("Could not update categories");
      loadOptions();
    }
  }, [loadOptions]);

  const optionsFor = useCallback(
    (group, fallback = []) => (options[group] && options[group].length ? options[group] : fallback),
    [options]
  );

  const value = useMemo(
    () => ({ content, options, optionsFor, editMode, setEditMode, isAdmin, saveText, saveOptions }),
    [content, options, optionsFor, editMode, setEditMode, isAdmin, saveText, saveOptions]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}
